<script setup lang="ts">
// TODO - Allow delete of variables

import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { AssemblyVariableOptions, AssemblyVariables } from '@/types/customTypes'
import { computed, ref, toRaw } from 'vue'
import BaseButton from './BaseButton.vue'
import BaseInput from './BaseInput.vue'
import BaseSelect from './BaseSelect.vue'

const props = defineProps<{ assemblyId: string }>()

const databaseStore = useDatabaseStore()
const assembly = computed(() => databaseStore.assemblies?.find((a) => a.id === props.assemblyId))
const newVariableData: AssemblyVariableOptions & { name: string } = {
  name: 'New_Variable',
  type: 'number',
  selectOptions: undefined,
  unit_of_measure: 'EA',
}
const currentVariable = ref<AssemblyVariableOptions & { name: string }>(
  structuredClone(newVariableData),
)
function setCurrentVariableToNewVariableData() {
  currentVariable.value = structuredClone(newVariableData)
}
const newSelectOptionName = ref('')
async function addSelectOption() {
  if (!currentVariable.value) throw Error('currentVariable.value is undefined')
  if (!currentVariable.value.selectOptions) {
    currentVariable.value.selectOptions = []
  }
  currentVariable.value.selectOptions.push(newSelectOptionName.value)
  newSelectOptionName.value = ''
}
async function saveOrUpdateVariables() {
  if (!currentVariable.value.name) throw toastThrowError('Variable name is required')
  const allowedVariableNameRegex = /^[A-Za-z_]+$/
  if (!allowedVariableNameRegex.test(currentVariable.value.name)) {
    throw toastThrowError('Variable names can only contain letters and underscores (_)')
  }
  // Check for variable name in variables
  if (!assembly.value)
    throw toastThrowError('Unable to locate assembly in memory. Reload and retry.')
  const assemblyCopy = structuredClone(toRaw(assembly.value))
  let variablesDB = assemblyCopy.variables as AssemblyVariables | null
  if (!variablesDB) {
    variablesDB = {}
  }
  variablesDB[currentVariable.value.name] = {
    type: currentVariable.value.type,
    selectOptions: currentVariable.value.selectOptions,
    unit_of_measure: currentVariable.value.unit_of_measure,
  }
  assemblyCopy.variables = variablesDB
  const result = await supabase
    .from('assembly')
    .update(assemblyCopy)
    .eq('id', props.assemblyId)
    .select()
    .single()
  if (result.error) throw toastThrowError('Error adding assembly variable', result.error)
  const index = databaseStore.assemblies?.findIndex((a) => a.id === props.assemblyId)
  if (index === undefined)
    throw toastThrowError('Unable to update variables locally. Refresh page.')
  databaseStore.assemblies?.splice(index, 1, result.data)
  toast('Saved variables', { type: 'success' })
}

async function deleteVariable() {
  if (!currentVariable.value) throw Error('currentVariable.value is undefined')
  const assemblyCopy = structuredClone(toRaw(assembly.value))
  const assemblyVariables = assemblyCopy?.variables as AssemblyVariables
  if (!assemblyVariables) {
    return
  }
  delete assemblyVariables[currentVariable.value.name]
  if (!assembly.value) throw Error('assembly.variable is undefined')
  assembly.value.variables = assemblyVariables
  const result = await supabase.from('assembly').update(assembly.value).eq('id', assembly.value.id)
  if (result.error) throw toastThrowError('Error deleting assembly variable', result.error)
}
</script>

<template>
  <div class="flex h-full w-full gap-2">
    <div class="flex w-2/3 flex-col gap-2 rounded-lg bg-gray-100 p-3">
      <h3 class="font-bold">Edit Variables</h3>
      <BaseButton @click="setCurrentVariableToNewVariableData"> New Variable </BaseButton>
      <div class="flex gap-2">
        <div class="flex flex-wrap gap-2 *:min-w-30">
          <!-- Variables editor -->
          <BaseInput name="Name" v-model="currentVariable.name" pattern="/^[A-Za-z_]+$/" />
          <BaseInput v-model="currentVariable.unit_of_measure" name="UOM" />
          <BaseSelect
            name="Type"
            v-model="currentVariable.type"
            :select-options="[
              { name: 'Checkbox', type: 'boolean' },
              { name: 'Number', type: 'number' },
              { name: 'Select', type: 'select' },
            ]"
            option-key="name"
            option-value="type"
            option-name="name"
          />
        </div>
      </div>
      <div
        class="flex flex-col gap-2 rounded-lg bg-gray-200 p-3"
        v-if="currentVariable.type === 'select'"
      >
        <h3 class="font-bold">Select Options</h3>
        <BaseInput v-model="newSelectOptionName" name="New Select Option Name" />
        <base-button @click="addSelectOption()">Add Option</base-button>
        <div class="flex h-full flex-col rounded-lg bg-white p-3">
          <span v-for="option in currentVariable.selectOptions" :key="option">
            {{ option }}
          </span>
        </div>
      </div>
      <BaseButton @click="saveOrUpdateVariables()">Save Variable</BaseButton>
      <BaseButton status="secondary" @click="deleteVariable()">Delete Variable</BaseButton>
    </div>
    <div class="flex w-1/3 flex-col gap-2 rounded-lg bg-gray-100 p-3">
      <!-- Variables List -->
      <h3 class="font-bold">Variables</h3>
      <div class="flex h-full flex-col gap-1 rounded-lg bg-white p-3 italic">
        <button
          v-for="(
            assemblyVariableOption, assemblyVariableName
          ) in assembly?.variables as AssemblyVariables"
          :key="assemblyVariableName"
          :class="`cursor-pointer rounded-lg p-1 text-left hover:bg-gray-200 ${currentVariable.name == assemblyVariableName ? 'bg-gray-200' : ''}`"
          @click="currentVariable = { name: assemblyVariableName, ...assemblyVariableOption }"
        >
          {{ assemblyVariableName }}
        </button>
      </div>
    </div>
  </div>
</template>
